import React, { CSSProperties } from "react";
import { mergeClasses } from "@fluentui/react-components";

import { useStackStyles } from "./styles";

type AsProp<T extends React.ElementType> = {
  as?: T;
};

type StackOwnProps<T extends React.ElementType> = AsProp<T> & {
  wrap?: boolean;
  inline?: boolean;
  gap?: CSSProperties["gap"];
  direction?: "row" | "column";
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  justify?: "start" | "center" | "end" | "stretch" | "between" | "around" | "evenly";
};

export type StackProps<T extends React.ElementType> = StackOwnProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof StackOwnProps<T>>;

export function Stack<T extends React.ElementType = "div">({
  as,
  wrap = false,
  inline = false,
  gap = 0,
  direction = "row",
  align,
  justify,
  className,
  style,
  children,
  ...props
}: StackProps<T>) {
  const styles = useStackStyles();
  const Component = as ?? "div";
  return (
    <Component
      className={mergeClasses(
        inline ? styles.inline : styles.base,
        wrap && styles.wrap,
        direction === "row" && styles.rowDirection,
        direction === "column" && styles.columnDirection,
        align === "start" && styles.alignStart,
        align === "center" && styles.alignCenter,
        align === "end" && styles.alignEnd,
        align === "stretch" && styles.alignStretch,
        align === "baseline" && styles.alignBaseline,
        justify === "start" && styles.justifyStart,
        justify === "center" && styles.justifyCenter,
        justify === "end" && styles.justifyEnd,
        justify === "stretch" && styles.justifyStretch,
        justify === "between" && styles.justifyBetween,
        justify === "around" && styles.justifyAround,
        justify === "evenly" && styles.justifyEvenly,
        className
      )}
      style={{ ...style, gap }}
      {...props}
    >
      {children}
    </Component>
  );
}
